/**
 * The newcomer path, agent first (owner feedback 2026-09-28): a person with no
 * FinderlyFix account pastes ONE prompt into their AI agent, and the agent
 * signs the site up, connects, installs the widget and sends a test lead,
 * following the public `/agent-setup.md` (agentSetupFiles.ts). The person's
 * part is answering the agent (who is responsible, OK to the Terms) and, later,
 * claiming the account from the email.
 *
 * One primary action (copy the prompt), one secondary (see what the agent
 * will do: four steps), one line of reassurance. The sign-in screen shows it
 * as the "My website" path of `NewcomerChooser` on the rail beside the email
 * form, `/docs/ai-setup` shows it at the top, and the homepage's /widget card
 * shows the same prompt and the same three paths.
 */
import { useId, useState } from 'react';
import { FeeSummary } from '../legal/FeeSummary';
import { CopyButton, Disclosure, Tabs } from '../ui/kit';
import { DOCS_PATH, newcomerCommand, newcomerPrompt } from './handoff';

export const AGENT_STEPS: ReadonlyArray<{ title: string; detail: string }> = [
  { title: 'Proves your site', detail: 'A file or a DNS record' },
  { title: 'Creates your account', detail: 'You’re named; you OK the Terms' },
  { title: 'Installs the widget', detail: 'And sends a test lead' },
  { title: 'You confirm by email', detail: 'Claim it within 14 days' },
];

export function AgentSteps() {
  return (
    <div className="stack-sm">
      <ol className="agent-journey">
        {AGENT_STEPS.map((s, i) => (
          <li key={s.title}>
            <span className="n" aria-hidden="true">
              {i + 1}
            </span>
            <span>
              <strong>{s.title}</strong>
              <span className="d">{s.detail}</span>
            </span>
          </li>
        ))}
      </ol>
      <p className="small agent-note">
        Claiming the account and reading leads stay with you. Not claimed in 14 days? The widget pauses until you claim it; nothing is
        deleted. <a href={`${DOCS_PATH}#no-account`}>Details</a>
      </p>
    </div>
  );
}

/** The prompt, its one copy button, and "See what your agent will do": the same on every surface. */
function AgentPromptBody({ lead }: { lead: string }) {
  const promptId = useId();
  const prompt = newcomerPrompt();
  return (
    <>
      <p className="small agent-note">{lead}</p>
      <blockquote className="agent-prompt" id={promptId}>
        {prompt}
      </blockquote>
      <div className="agent-actions">
        <CopyButton text={prompt} label="Copy the prompt for your agent" className="btn btn-primary" describedBy={promptId} />
      </div>
      <p className="small agent-note">It asks you who’s responsible and for your OK to the Terms, then does the rest.</p>
      <Disclosure label="See what your agent will do" className="agent-will">
        <AgentSteps />
      </Disclosure>
    </>
  );
}

const PROMPT_LEAD = 'Paste this into Claude Code, Codex, Cursor or any coding agent, in your website’s repo.';

export function AgentPromptCard({ onRail = false, commands = false }: { onRail?: boolean; commands?: boolean }) {
  const headingId = useId();
  return (
    <section className={`agent-first${onRail ? ' on-rail' : ''}`} aria-labelledby={headingId}>
      <h2 id={headingId}>New here? Let your AI agent set it up</h2>
      <AgentPromptBody lead={PROMPT_LEAD} />
      {commands ? (
        <div className="stack-sm agent-cmds">
          <p className="small agent-note">Or start it from a terminal at your website repo’s root:</p>
          <CommandLine code={newcomerCommand('claude')} label="Claude Code" />
          <CommandLine code={newcomerCommand('codex')} label="Codex" />
        </div>
      ) : null}
    </section>
  );
}

/**
 * The sign-in rail's chooser (owner feedback 2026-09-28: "this doesn't offer
 * breakdown of if they need a new website with us/testing out the widget vs
 * the full install on the repo"). Three paths, one primary action each:
 *
 *   - Get a free page: a page we host (finderlyfix.com/p/…), listed first
 *     (owner decision 2026-10-20: contractors are the audience the homepage
 *     sends here). Agent sign-up has to prove a website, so this path is the
 *     email/Google form beside it; the button remembers the choice
 *     (setup/startPath.ts) so org setup offers "A free page from us" first,
 *     and moves focus to the email field;
 *   - Add to my website: the one agent prompt (the full install), still the
 *     tab that opens first unless `?start=page` says otherwise;
 *   - Just try it: the live demo on finderlyfix.com/widget, no account.
 */
export const LIVE_DEMO_URL = 'https://finderlyfix.com/widget';
export const DEMO_PARTNER_URL = 'https://summit.25x.codes';

export type NewcomerPath = 'try' | 'page' | 'site';

export function NewcomerChooser({
  onStartPage,
  onPath,
  google = false,
  initial = 'site',
}: {
  onStartPage: () => void;
  /** The tab in view, so the rail's pitch can follow it. */
  onPath?: (path: NewcomerPath) => void;
  google?: boolean;
  initial?: NewcomerPath;
}) {
  const headingId = useId();
  const [path, setPathState] = useState<NewcomerPath>(initial);
  const setPath = (next: NewcomerPath) => {
    setPathState(next);
    onPath?.(next);
  };
  return (
    <section className="agent-first on-rail newcomer" aria-labelledby={headingId}>
      <h2 id={headingId}>New here? Pick how to start</h2>
      <Tabs<NewcomerPath>
        label="How to start"
        value={path}
        onChange={setPath}
        tabs={[
          {
            value: 'page',
            label: 'Get a free page',
            panel: (
              <div className="stack-sm">
                <p className="small agent-note">
                  A free page at <strong>finderlyfix.com/p/your-name</strong>: your photos, services, reviews, hours and a quote form,
                  with a link and QR code to share.
                </p>
                <ol className="newcomer-steps small">
                  <li>Sign in with your email{google ? ' or Google' : ''}.</li>
                  <li>Tell us your business name.</li>
                  <li>Pick your address and go live.</li>
                </ol>
                <FeeSummary className="small agent-note" />
                <div className="agent-actions">
                  <button type="button" className="btn btn-primary" onClick={onStartPage}>
                    Start with my email
                  </button>
                </div>
              </div>
            ),
          },
          {
            value: 'site',
            label: 'Add to my website',
            panel: (
              <div className="stack-sm">
                <AgentPromptBody lead={PROMPT_LEAD} />
              </div>
            ),
          },
          {
            value: 'try',
            label: 'Just try it',
            panel: (
              <div className="stack-sm">
                <p className="small agent-note">Try the live widget first: a photo, a sound or Talk live. No account.</p>
                <div className="agent-actions">
                  <a className="btn btn-primary" href={LIVE_DEMO_URL} target="_blank" rel="noopener noreferrer">
                    Open the live demo <span aria-hidden="true">↗</span>
                  </a>
                </div>
                <p className="small agent-note">
                  Or see it on a demo partner site:{' '}
                  <a href={DEMO_PARTNER_URL} target="_blank" rel="noopener noreferrer">
                    summit.25x.codes
                  </a>
                </p>
              </div>
            ),
          },
        ]}
      />
    </section>
  );
}

function CommandLine({ code, label }: { code: string; label: string }) {
  const id = useId();
  return (
    <div className="keyline">
      <span className="small muted" id={id}>
        {label}
      </span>
      <code className="key">{code}</code>
      <CopyButton text={code} label="Copy" className="btn btn-sm" describedBy={id} />
    </div>
  );
}
